import { createTheme, responsiveFontSizes, Theme } from '@mui/material/styles';
import { esES } from '@mui/x-data-grid/locales';
import { esES as pickersEsEs } from '@mui/x-date-pickers/locales';
import { esES as coreEsEs } from '@mui/material/locale';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

// ============================================
// 1. Definiciones de Constantes del Tema
// ============================================
const borderRadius = 10;

const palette = {
    primary: {
        main: '#2B2B2B',
        light: '#3B3B3B',
        dark: '#1C1C1C',
        contrastText: '#FFFFFF'
    },
    secondary: {
        main: '#FFFFFF',
        light: '#FFFFFF',
        dark: '#FFFFFF',
        contrastText: '#1C1C1C'
    },
    error: {
        main: '#D32F2F'
    },
    warning: {
        main: '#FFA000'
    },
    info: {
        main: '#183CAD'
    },
    success: {
        main: '#388E3C'
    },
    background: {
        default: '#F4F4F9',
        paper: '#FFFFFF'
    },
    divider: '#C8CCDD',
    text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: 'rgba(0, 0, 0, 0.6)',
        disabled: 'rgba(0, 0, 0, 0.38)'
    }
};

const typography = {
    fontFamily: 'Inter, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontSize: '2.7rem', fontWeight: 700 },
    h2: { fontSize: '2.2rem', fontWeight: 700 },
    h3: { fontSize: '1.9rem', fontWeight: 700 },
    h4: { fontSize: '1.6rem', fontWeight: 600 },
    h5: { fontSize: '1.45rem', fontWeight: 600 },
    h6: { fontSize: '1.1rem', fontWeight: 600 },
    body1: { fontSize: '1.05rem' },
    body2: { fontSize: '0.95rem' },
    button: {
        textTransform: 'none',
        fontWeight: 600
    }
};

// ============================================
// 2. Creación del Tema
// ============================================
let theme = createTheme(esES, pickersEsEs, coreEsEs, {
    palette,
    typography,
    shape: {
        borderRadius: borderRadius
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: palette.background.default,
                    scrollbarColor: `${palette.divider} ${palette.background.default}`,
                    scrollbarWidth: 'thin'
                },
                '*::-webkit-scrollbar': {
                    width: '10px',
                    height: '10px'
                },
                '*::-webkit-scrollbar-track': {
                    backgroundColor: palette.background.paper
                },
                '*::-webkit-scrollbar-thumb': {
                    backgroundColor: palette.background.default,
                    borderRadius: '10px',
                    border: `2px solid ${palette.background.paper}`
                },
                '*::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: palette.divider // Reutilizando el nuevo color aquí
                }
            }
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true
            },
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    fontSize: typography.body2.fontSize,
                    fontWeight: typography.button.fontWeight,
                    padding: '10px 16px',
                    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                        fontSize: 24
                    }
                }),
                containedPrimary: {
                    '&:hover': {
                        backgroundColor: palette.primary.light
                    }
                },
                containedSecondary: {
                    '&:hover': {
                        backgroundColor: palette.primary.light
                    }
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ ownerState, theme }: { ownerState: any; theme: Theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    padding: 10,
                    ...(ownerState.color === 'primary' && {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.light
                        }
                    }),
                    ...(ownerState.color === 'secondary' && {
                        backgroundColor: theme.palette.primary.contrastText,
                        color: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.contrastText
                        }
                    })
                })
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    padding: '12px 16px !important'
                },
                input: {
                    padding: '0 !important'
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiInputLabel-root': {
                        transform: 'translate(16px, 12px) scale(1)',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(15px, -8px) scale(0.75)'
                        }
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    fontSize: typography.body2.fontSize,
                    backgroundColor: palette.background.paper
                })
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: 'none',
                    padding: theme.spacing(1),
                    '&:before': {
                        display: 'none'
                    }
                })
            }
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    border: 'none',
                    backgroundColor: palette.background.paper,
                    borderRadius: borderRadius
                },
                cell: {
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: typography.body2.fontSize,
                    borderBottom: `2px solid ${palette.divider}`
                },
                columnHeaders: {
                    borderBottom: `2px solid ${palette.divider}`,
                    backgroundColor: palette.background.paper
                },
                footerContainer: {
                    borderTop: 'none'
                }
            }
        },
        MuiCard: {
            defaultProps: {
                elevation: 0
            },
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    padding: theme.spacing(2),
                    boxShadow: 'none'
                })
            }
        },
        MuiTabs: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    position: 'relative',
                    overflow: 'visible',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -1,
                        left: 0,
                        right: 0,
                        height: 2,
                        backgroundColor: theme.palette.divider,
                        zIndex: 0
                    }
                }),
                indicator: ({ theme }: { theme: Theme }) => ({
                    height: 4,
                    bottom: -1,
                    backgroundColor: theme.palette.primary.main,
                    zIndex: 1,
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
                })
            }
        },
        MuiTab: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    textTransform: 'none',
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    transition: 'color 0.3s ease, transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        color: theme.palette.text.primary
                    },
                    '&.Mui-selected': {
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        transform: 'scale(1.05)'
                    }
                })
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    borderColor: theme.palette.divider,
                    borderBottomWidth: 2,
                    borderRightWidth: 2
                })
            }
        }
    }
});

theme = responsiveFontSizes(theme);

export default theme;
